import React, { useState, useEffect } from "react";
import { getPaginationRowModel, createColumnHelper, useReactTable, flexRender, getCoreRowModel, getFilteredRowModel } from "@tanstack/react-table";
import { NblocksButton } from "./NblocksButton";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/solid";
import { ListUsersDocument, UpdateTenantDocument } from "../../gql/graphql";
import { useMutation, useQuery } from "@apollo/client";
import { UserTableRowComponent } from "./UserTableRowComponent";
import { ModalComponent } from "./ModalComponent";
import { SafeUserNameComponent } from "./SafeUserNameComponent";
import { SkeletonLoader } from "./SkeletonLoader";
import { useTranslation } from "react-i18next";
import { TogglerComponent } from "./TogglerComponent";
import { useApp } from "../../hooks/app-context";
import { TextComponent } from "./TextComponent";

/**
 *
 */

const UserListTableComponent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    t
  } = useTranslation();
  const {
    mfaEnabled: mfaAppEnabled
  } = useApp();
  const [mfaTenantEnabled, setMfaTenantEnabled] = useState(false);
  const [modalState, setModalState] = useState({
    heading: "",
    description: "",
    type: "primary",
    onPrimaryClick: () => {}
  });
  const {
    data,
    loading
  } = useQuery(ListUsersDocument);
  const [updateTenantMutation, updateTenantProps] = useMutation(UpdateTenantDocument);
  const {
    data: updateTenantData
  } = updateTenantProps;
  useEffect(() => {
    setMfaTenantEnabled(updateTenantData ? !!updateTenantData.updateTenant.mfa : !!data?.getTenant.mfa);
  }, [data, updateTenantData]);
  const onMfaTenantEnabledChange = enabledState => {
    updateTenantMutation({
      variables: {
        tenant: {
          mfa: enabledState
        }
      }
    });
  };
  const columnHelper = createColumnHelper();
  const columns = [columnHelper.accessor("fullName", {
    header: t("Name"),
    footer: props => props.column.id,
    cell: info => /*#__PURE__*/React.createElement("span", {
      className: "font-medium text-gray-900"
    }, /*#__PURE__*/React.createElement(SafeUserNameComponent, {
      name: info.getValue()
    }))
  }), columnHelper.accessor("enabled", {
    header: t("Status"),
    footer: props => props.column.id,
    cell: info => info.getValue() ? t("Active") : t("Deactivated")
  }), columnHelper.accessor("role", {
    header: t("Role"),
    footer: props => props.column.id,
    // t(RoleName)
    cell: info => info.getValue() ? t(info.getValue()) : info.getValue()
  }), columnHelper.accessor("email", {
    header: t("Email Address"),
    footer: props => props.column.id,
    cell: info => info.getValue(),
    meta: {
      editable: true
    }
  }), columnHelper.display({
    id: "edit"
  })];
  const getModalButtons = type => {
    switch (type) {
      case "danger":
        return /*#__PURE__*/React.createElement("div", {
          className: "flex flex-col-reverse md:flex-row md:justify-between mt-5 gap-3"
        }, /*#__PURE__*/React.createElement(NblocksButton, {
          size: "md",
          className: "w-full",
          type: "tertiary",
          onClick: () => setIsOpen(false)
        }, t("Cancel")), /*#__PURE__*/React.createElement(NblocksButton, {
          size: "md",
          className: "w-full",
          type: "danger",
          onClick: () => {
            modalState.onPrimaryClick();
          }
        }, t("Delete")));
      default:
        return /*#__PURE__*/React.createElement("div", {
          className: "flex flex-col-reverse md:flex-row md:justify-between mt-5 gap-3"
        }, /*#__PURE__*/React.createElement(NblocksButton, {
          size: "md",
          className: "w-full",
          type: "tertiary",
          onClick: () => setIsOpen(false)
        }, t("Cancel")), /*#__PURE__*/React.createElement(NblocksButton, {
          size: "md",
          className: "w-full",
          type: "primary",
          onClick: () => {
            modalState.onPrimaryClick();
            setIsOpen(false);
          }
        }, t("Save changes")));
    }
  };
  const table = useReactTable({
    data: data?.listUsers ? data.listUsers : [],
    columns,
    // Pipeline
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: true
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "w-full h-full overflow-x-auto grow"
  }, mfaAppEnabled && data && /*#__PURE__*/React.createElement("div", {
    className: "flex items-center px-6 mb-5"
  }, /*#__PURE__*/React.createElement(TextComponent, {
    className: "mr-4"
  }, t("Two-factor authentication")), /*#__PURE__*/React.createElement(TogglerComponent, {
    enabled: mfaTenantEnabled,
    setEnabled: value => onMfaTenantEnabledChange(value)
  })), /*#__PURE__*/React.createElement("table", {
    className: "w-full text-left"
  }, /*#__PURE__*/React.createElement("thead", {
    className: "h-11 bg-gray-50 border-t border-b border-gray-200"
  }, table.getHeaderGroups().map(headerGroup => /*#__PURE__*/React.createElement("tr", {
    key: headerGroup.id
  }, headerGroup.headers.map(header => /*#__PURE__*/React.createElement("th", {
    key: header.id
  }, header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())))))), /*#__PURE__*/React.createElement("tbody", null, table.getRowModel().rows.length === 0 && loading && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(SkeletonLoader, {
    className: "h-8 w-full rounded-md"
  })), /*#__PURE__*/React.createElement("td", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(SkeletonLoader, {
    className: "h-8 w-full rounded-md"
  })), /*#__PURE__*/React.createElement("td", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(SkeletonLoader, {
    className: "h-8 w-full rounded-md"
  })), /*#__PURE__*/React.createElement("td", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(SkeletonLoader, {
    className: "h-8 w-full rounded-md"
  })), /*#__PURE__*/React.createElement("td", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(SkeletonLoader, {
    className: "h-8 w-full rounded-md"
  }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(SkeletonLoader, {
    className: "h-8 w-full rounded-md"
  })), /*#__PURE__*/React.createElement("td", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(SkeletonLoader, {
    className: "h-8 w-full rounded-md"
  })), /*#__PURE__*/React.createElement("td", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(SkeletonLoader, {
    className: "h-8 w-full rounded-md"
  })), /*#__PURE__*/React.createElement("td", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(SkeletonLoader, {
    className: "h-8 w-full rounded-md"
  })), /*#__PURE__*/React.createElement("td", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(SkeletonLoader, {
    className: "h-8 w-full rounded-md"
  }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(SkeletonLoader, {
    className: "h-8 w-full rounded-md"
  })), /*#__PURE__*/React.createElement("td", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(SkeletonLoader, {
    className: "h-8 w-full rounded-md"
  })), /*#__PURE__*/React.createElement("td", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(SkeletonLoader, {
    className: "h-8 w-full rounded-md"
  })), /*#__PURE__*/React.createElement("td", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(SkeletonLoader, {
    className: "h-8 w-full rounded-md"
  })), /*#__PURE__*/React.createElement("td", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(SkeletonLoader, {
    className: "h-8 w-full rounded-md"
  })))), table.getRowModel().rows.map((row, index) => /*#__PURE__*/React.createElement(UserTableRowComponent, {
    row: row,
    key: index,
    setModalState: setModalState,
    setModalIsOpen: setIsOpen
  }))))), /*#__PURE__*/React.createElement("div", {
    className: "border-t px-6 pb-4 pt-3.5 flex items-center justify-between"
  }, /*#__PURE__*/React.createElement(NblocksButton, {
    size: "lg",
    type: "tertiary",
    className: "hidden md:flex items-center justify-center",
    onClick: () => table.previousPage(),
    disabled: !table.getCanPreviousPage()
  }, /*#__PURE__*/React.createElement(ArrowLeftIcon, {
    className: "h-6 w-6 inline-block mr-2"
  }), t("Previous")), /*#__PURE__*/React.createElement(NblocksButton, {
    type: "tertiary",
    className: "md:hidden items-center justify-center p-2",
    onClick: () => table.previousPage(),
    disabled: !table.getCanPreviousPage()
  }, /*#__PURE__*/React.createElement(ArrowLeftIcon, {
    className: "h-6 w-6 inline-block"
  })), /*#__PURE__*/React.createElement("span", {
    className: "flex items+center gap-1"
  }, /*#__PURE__*/React.createElement("p", null, "Page"), /*#__PURE__*/React.createElement("strong", null, table.getState().pagination.pageIndex + 1, " of", " ", table.getPageCount())), /*#__PURE__*/React.createElement(NblocksButton, {
    type: "tertiary",
    size: "lg",
    className: "hidden md:flex items-center justify-center",
    onClick: () => table.nextPage(),
    disabled: !table.getCanNextPage()
  }, t("Next"), /*#__PURE__*/React.createElement(ArrowRightIcon, {
    className: "h-6 w-6 inline-block md:ml-2"
  })), /*#__PURE__*/React.createElement(NblocksButton, {
    type: "tertiary",
    className: "md:hidden items-center justify-center p-2",
    onClick: () => table.nextPage(),
    disabled: !table.getCanNextPage()
  }, /*#__PURE__*/React.createElement(ArrowRightIcon, {
    className: "h-6 w-6 inline-block"
  }))), /*#__PURE__*/React.createElement(ModalComponent, {
    isOpen: isOpen,
    setIsOpen: setIsOpen,
    icon: modalState.icon,
    iconType: modalState.type,
    heading: modalState.heading,
    description: modalState.description
  }, getModalButtons(modalState.type)));
};
export { UserListTableComponent };